import React, { useEffect, useMemo, useState } from "react";
import { VendorItemDetails } from "types/vendor";
import { updateVendorItemQuantity, removeVendorItemDetails } from "api/vendorsApi";
import { Button } from "components/button";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useModalContext } from "components/modal";
import { toast } from "react-toastify";
import styles from "./VendorItemDetail.module.scss";

const formatCentsToPriceInput = (cents: number | null | undefined) =>
  cents != null ? (cents / 100).toFixed(2) : "";

const parsePriceInputToCents = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseFloat(trimmed);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100);
};

interface IProps {
  item: VendorItemDetails;
  vendorId?: number;
}

const VendorItemDetail = ({ item, vendorId }: IProps) => {
  const { t } = useTranslation();
  const tr = (key: string) =>
    t(`views.vendors.VendorBody.VendorItems.VendorItemDetail.VendorItemDetail.${key}`);
  
  const queryClient = useQueryClient();
  const { setModal } = useModalContext();

  // Destructure fields
  const {
    id,
    vendor_part_number,
    price,
    item_name,
    long_description,
    model,
    imageUrl,
    last_update,
    latest_status,
    siteone_description,
    siteone_item_number,
    siteone_price
  } = item;

  // Use fallback logic
  // const description = siteone_description || long_description;

  const storedPriceCents = siteone_price ?? price ?? null;
  const storedVendorPartNumber =
    (siteone_item_number || vendor_part_number || "").trim();

  const [newVendorPartNumber, setNewVendorPartNumber] = useState(
    siteone_item_number || vendor_part_number || ""
  );
  const [savedVendorPartNumber, setSavedVendorPartNumber] = useState(
    storedVendorPartNumber
  );
  const [savedPriceCents, setSavedPriceCents] = useState<number | null>(storedPriceCents);
  const [newPrice, setNewPrice] = useState(() => formatCentsToPriceInput(storedPriceCents));
  const [error, setError] = useState(false);

  useEffect(() => {
    const cents = siteone_price ?? price ?? null;
    const part = (siteone_item_number || vendor_part_number || "").trim();
    setSavedPriceCents(cents);
    setNewPrice(formatCentsToPriceInput(cents));
    setSavedVendorPartNumber(part);
    setNewVendorPartNumber(siteone_item_number || vendor_part_number || "");
    setError(false);
  }, [id, siteone_price, price, siteone_item_number, vendor_part_number]);

  const isPriceDirty = useMemo(() => {
    return parsePriceInputToCents(newPrice) !== (savedPriceCents ?? null);
  }, [newPrice, savedPriceCents]);

  const isPartNumberDirty = useMemo(() => {
    return newVendorPartNumber.trim() !== savedVendorPartNumber;
  }, [newVendorPartNumber, savedVendorPartNumber]);

  const priceInputInvalid = useMemo(() => {
    const trimmed = newPrice.trim();
    return (
      trimmed !== "" &&
      (Number.isNaN(parseFloat(trimmed)) || parsePriceInputToCents(newPrice) === null)
    );
  }, [newPrice]);

  const effectivePriceCents = useMemo(() => {
    return parsePriceInputToCents(newPrice) ?? savedPriceCents;
  }, [newPrice, savedPriceCents]);

  const canSave =
    siteone_item_number === null &&
    siteone_price === null &&
    !priceInputInvalid &&
    effectivePriceCents != null &&
    (isPriceDirty || isPartNumberDirty);

  const handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPrice(value);
    const trimmed = value.trim();
    setError(trimmed !== "" && Number.isNaN(parseFloat(trimmed)));
  };

  const handleSave = async () => {
    if (!canSave) return;
    const priceCents = effectivePriceCents;
    if (priceCents == null) return;

    try {
      const response = await updateVendorItemQuantity(
        id,
        priceCents,
        newVendorPartNumber.trim() || null
      );

      if (response) {
        const cents = response.price ?? priceCents;
        setSavedPriceCents(cents);
        setNewPrice(formatCentsToPriceInput(cents));
        setSavedVendorPartNumber(newVendorPartNumber.trim());
        queryClient.invalidateQueries(["vendors_items", vendorId]);
        toast.success(tr("Changes saved"));
      }
    } catch (saveError) {
      console.error("Error saving vendor item price:", saveError);
      toast.error(tr("Error saving price"));
    }
  };

  const handleRemove = async () => {
    try {
      await removeVendorItemDetails(id);
      // Refresh the vendor items list
      queryClient.invalidateQueries(["vendors_items", vendorId]);
      toast.success(tr("Item removed successfully"));
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error(tr("Error removing item"));
    }
  };


  const showRemoveConfirmation = () => {
    setModal({
      component: (
        <div className="">
          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-gray-700 leading-relaxed">
              {tr("Are you sure you want to remove this item from the vendor?")}
            </p>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              primary
              onClick={() => {
                setModal();
                handleRemove();
              }}
              // className="eserv-btn eserv-btn-danger"
            >
              {tr("Remove")}
            </Button>
            <Button
              onClick={() => setModal()}
              // className="eserv-btn eserv-btn-secondary"
            >
              {tr("Cancel")}
            </Button>
          </div>
        </div>
      ),
      label: tr("Remove Item"),
    });
  };


  return (
    <div className="flex items-start justify-between gap-4 bg-gray-100 rounded border p-4 mb-3">
      {imageUrl && (
        <img
          src={imageUrl}
          alt={item_name}
          className="w-16 h-16 object-contain rounded border"
        />
      )}

      <div className="flex flex-col flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-700">
          {model}
          {vendor_part_number && (
            <span className="ml-1 text-xs text-gray-500">
              (vend.: {vendor_part_number})
            </span>
          )}
        </div>
        <div className="text-md font-bold text-gray-900">{siteone_item_number || item_name}</div>
        <div className="text-sm text-gray-600 truncate">{siteone_description || long_description}</div>
        <div></div>
          {siteone_description && (
            <div className="bg-[#79ff79] rounded-[5px] w-fit text-center px-[5px] py-[5px] font-bold border-[2px] border-black">
              {tr("Linked SiteOne Item")}
            </div>
          )}
      </div>

      <div className="flex flex-col items-end gap-2 text-xs text-right min-w-[240px]">

        <div className="flex mb-2 items-center justify-end">
          <div className="flex flex-col flex-1">
            <label className="text-xs text-gray-500 mb-1 text-center">{siteone_item_number ? "SiteOne" : "Vendor"} Part Number</label>
            <input
              type="text"
              value={newVendorPartNumber}
              onChange={(e) => setNewVendorPartNumber(e.target.value)}
              className="border px-2 py-1 rounded text-sm w-full"
              disabled={siteone_item_number !== null}
            />
          </div>

          <div className="flex flex-col w-24">
            <label className="text-xs text-gray-500 mb-1 text-center">Price</label>
            <input
              type="text"
              value={newPrice}
              onChange={handleChangePrice}
              className={`border px-2 py-1 rounded text-sm ${error || priceInputInvalid ? "border-red-500" : ""}`}
              disabled={siteone_price !== null}
            />
          </div>

          <div className="ml-2 self-end flex items-center gap-2">
            {siteone_item_number === null && (
              <Button
                className={!canSave ? styles.saveButtonFaded : undefined}
                style={{ height: "2rem", minWidth: "80px" }}
                primary
                onClick={handleSave}
                disabled={!canSave}
              >
                {tr("Save")}
              </Button>
            )}

            <Button
              style={{
                backgroundColor: "red",
                color: "white",
                height: "2rem",
                minWidth: "80px",
              }}
              onClick={showRemoveConfirmation}
            >
              {tr("Remove")}
            </Button>
          </div>



        </div>
        {last_update && (
          <div className="text-gray-500">
            <strong>Last Updated:</strong>{" "}
            {new Date(last_update).toLocaleDateString()}
          </div>
        )}
        {latest_status && (
          <div className="text-gray-500">
            <strong>Status:</strong> {latest_status}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorItemDetail;
